/**
 * Détecte le type MIME d'un fichier basé sur son extension
 * @param filename - Le nom du fichier avec extension
 * @returns Le type MIME correspondant ou "application/octet-stream" par défaut
 */
export function getMimeTypeFromExtension(filename: string): string {
	const extension = filename.toLowerCase().split(".").pop();

	const mimeTypes: Record<string, string> = {
		// Documents PDF
		pdf: "application/pdf",

		// Images
		jpg: "image/jpeg",
		jpeg: "image/jpeg",
		png: "image/png",
		gif: "image/gif",
		bmp: "image/bmp",
		webp: "image/webp",
		svg: "image/svg+xml",
		tiff: "image/tiff",
		tif: "image/tiff",

		// Documents Office
		doc: "application/msword",
		docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
		xls: "application/vnd.ms-excel",
		xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		ppt: "application/vnd.ms-powerpoint",
		pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",

		// Archives
		zip: "application/zip",
		rar: "application/vnd.rar",
		"7z": "application/x-7z-compressed",
		tar: "application/x-tar",
		gz: "application/gzip",

		// Texte
		txt: "text/plain",
		csv: "text/csv",
		json: "application/json",
		xml: "application/xml",
		html: "text/html",
		htm: "text/html",

		// Autres formats courants
		rtf: "application/rtf",
		odt: "application/vnd.oasis.opendocument.text",
		ods: "application/vnd.oasis.opendocument.spreadsheet",
		odp: "application/vnd.oasis.opendocument.presentation",
	};

	return extension
		? mimeTypes[extension] || "application/octet-stream"
		: "application/octet-stream";
}

/**
 * Détecte le type MIME d'un fichier en combinant l'extension et une validation du contenu
 * @param file - Le fichier à analyser
 * @param filename - Le nom du fichier (optionnel, utilise file.name par défaut)
 * @returns Promise<string> - Le type MIME détecté
 */
export async function getMimeTypeFromFileContent(
	file: File | Blob,
	filename?: string,
): Promise<string> {
	const name = filename || ("name" in file && file.name) || "";
	const extensionBasedMimeType = getMimeTypeFromExtension(name);

	// Pour les fichiers sans extension ou avec type générique, on essaie de détecter le contenu
	if (
		!name.includes(".") ||
		extensionBasedMimeType === "application/octet-stream"
	) {
		return await detectMimeTypeFromContent(file);
	}

	// Validation du contenu pour les types critiques
	const detectedFromContent = await detectMimeTypeFromContent(file);

	// Si la détection de contenu donne un résultat spécifique et différent, on la privilégie
	if (
		detectedFromContent !== "application/octet-stream" &&
		detectedFromContent !== extensionBasedMimeType &&
		isReliableContentDetection(detectedFromContent)
	) {
		return detectedFromContent;
	}

	return extensionBasedMimeType;
}

/**
 * Détecte le type MIME basé sur les premiers octets du fichier (magic numbers)
 * @param file - Le fichier à analyser
 * @returns Promise<string> - Le type MIME détecté ou "application/octet-stream"
 */
async function detectMimeTypeFromContent(file: File | Blob): Promise<string> {
	// Lire les premiers octets du fichier
	const chunk = file.slice(0, 8);
	const arrayBuffer = await chunk.arrayBuffer();
	const bytes = new Uint8Array(arrayBuffer);

	// Signatures de fichiers (magic numbers)

	// PDF: commence par "%PDF"
	if (
		bytes[0] === 0x25 &&
		bytes[1] === 0x50 &&
		bytes[2] === 0x44 &&
		bytes[3] === 0x46
	) {
		return "application/pdf";
	}

	// PNG: commence par le signature PNG
	if (
		bytes[0] === 0x89 &&
		bytes[1] === 0x50 &&
		bytes[2] === 0x4e &&
		bytes[3] === 0x47
	) {
		return "image/png";
	}

	// JPEG: commence par 0xFF 0xD8
	if (bytes[0] === 0xff && bytes[1] === 0xd8) {
		return "image/jpeg";
	}

	// GIF: commence par "GIF"
	if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) {
		return "image/gif";
	}

	// ZIP: commence par "PK"
	if (bytes[0] === 0x50 && bytes[1] === 0x4b) {
		return "application/zip";
	}

	return "application/octet-stream";
}

/**
 * Détermine si une détection de contenu est fiable
 * @param mimeType - Le type MIME détecté
 * @returns boolean - True si la détection est considérée comme fiable
 */
function isReliableContentDetection(mimeType: string): boolean {
	const reliableTypes = [
		"application/pdf",
		"image/png",
		"image/jpeg",
		"image/gif",
		"application/zip",
	];

	return reliableTypes.includes(mimeType);
}
